/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { useAtlassianUserContext } from '../../../../hooks/use-user-context';
import { CollectFeedbackV2 } from '../../CollectFeedback';
import { messages } from '../messages';
import { useSendFeedback } from '../useSendFeedback';

const styles = cssMap({
	feedbackButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		border: 'none',
		borderRadius: token('radius.medium'),
		backgroundColor: 'transparent',
		color: 'inherit',
		font: 'inherit',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'@media (max-width: 30rem)': {
			display: 'none',
		},
		transition: token('motion.button.hovered'),
	},
	// Measured live, logged in: below 936px the hamburger and the site
	// switcher crowd the right-hand cluster and "Send feedback" wrapped
	// onto two lines. Only the switcher's label is meant to absorb the
	// squeeze, so this opts out of shrinking.
	feedbackButtonFixedWidth: {
		whiteSpace: 'nowrap',
		flexShrink: 0,
	},
	feedbackButtonDiscovery: {
		backgroundColor: token('color.background.discovery.bold'),
		color: token('color.text.inverse'),
		'&:hover': {
			backgroundColor: token('color.background.discovery.bold.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.discovery.bold.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
});

type FeedbackControlProps = {
	/** Draws the button in the bold discovery colour, to invite feedback on a page being trialled. */
	isHighlighted?: boolean;
	testId?: string;
};

export const FeedbackControl = ({
	isHighlighted = false,
	testId = 'feedback-control',
}: FeedbackControlProps): JSX.Element => {
	const { identity: user } = useAtlassianUserContext();
	const { isFeedbackOpen, openFeedback, closeFeedback } = useSendFeedback();

	return (
		<Fragment>
			<button
				type="button"
				css={[
					styles.feedbackButton,
					styles.feedbackButtonFixedWidth,
					isHighlighted && styles.feedbackButtonDiscovery,
				]}
				onClick={openFeedback}
				data-testid={testId}
			>
				<FormattedMessage {...messages.sendFeedback} />
			</button>
			<CollectFeedbackV2
				atlassianAccountId={user.accountId}
				name={user.name}
				isOpen={isFeedbackOpen}
				onClose={closeFeedback}
			/>
		</Fragment>
	);
};
